import { useState, useCallback } from 'react';
import useAssociativeArray from './useAssociativeArray';

const validityProperties = [
  'badInput',
  'customError',
  'patternMismatch',
  'rangeOverflow',
  'rangeUnderflow',
  'stepMismatch',
  'tooLong',
  'tooShort',
  'typeMismatch',
  'valid',
  'valueMissing',
];

const useForm = (options = {}) => {
  const { defaults = {}, onSubmit, debounce = 1000 } = options;
  const [values, addValue, setValues] = useAssociativeArray(defaults);
  const [validations, addValidation, setValidations] = useAssociativeArray({});
  const [loading, setLoading] = useState();
  const [error, setError] = useState();
  const [timeoutObj, setTimeoutObj] = useState({});

  const formatValidity = (validity, dirty) =>
    validityProperties.reduce(
      (aggregate, key) => ({
        ...aggregate,
        [key]: validity[key],
      }),
      {
        dirty,
      }
    );

  const changed = useCallback(
    () =>
      !!Object.keys(values).find(key => {
        let val = values[key];
        let compare = defaults[key];

        if (val instanceof Date && compare instanceof Date) {
          val = val.getTime();
          compare = compare.getTime();
        }

        return val !== compare;
      }),
    [defaults, values]
  );

  const setValidate = obj => {
    const {
      name,
      validity = {
        valid: true,
      },
    } = obj.target || obj;
    addValidation(name, formatValidity(validity, true));
  };

  const setValue = (obj, validate = true) => {
    const data = obj.target || obj;
    const { name, checked, type } = data;
    let { value } = data;

    if (type === 'checkbox') {
      value = checked;
    }

    if (!name) {
      return value;
    }

    addValue(name, value);
    if (validate) {
      setValidate(obj);
    }
    return value;
  };

  const setValueDebounce = (obj, customDebounce) => {
    const data = obj.target || obj;
    setValue(obj, false);

    if (timeoutObj[data.name]) {
      clearTimeout(timeoutObj[data.name]);
    }

    const timeout = setTimeout(() => {
      setValidate(data);
    }, customDebounce || debounce);

    setTimeoutObj(timeouts => ({
      ...timeouts,
      [data.name]: timeout,
    }));
  };

  const validation = [...validityProperties, 'dirty'].reduce(
    (aggregate, key) => ({
      ...aggregate,
      [key]: name => {
        if (!validations[name]) return false;
        return validations[name][key];
      },
    }),
    {}
  );

  const submit = useCallback(
    async event => {
      try {
        const { target } = event;

        event.preventDefault();
        const valid = target.checkValidity();

        if (!valid) {
          const elements = [
            ...target.querySelectorAll('input, select, textarea'),
          ];
          const vals = elements.reduce((aggregate, e) => {
            const { name, validity } = e;
            return name
              ? {
                  ...aggregate,
                  [name]: formatValidity(validity, true),
                }
              : aggregate;
          }, {});
          setValidations(vals);
          return;
        }

        setLoading(true);
        await onSubmit(values, event);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [onSubmit, setValidations, values]
  );

  const reset = useCallback(() => {
    setValues(defaults);
    setValidations({});
    setError();
  }, [defaults, setValidations, setValues]);

  const valid = useCallback(
    () => Object.keys(validations).every(key => validations[key].valid),
    [validations]
  );

  return {
    values,
    setValue,
    setValues,
    setValueDebounce,
    setValidate,
    setValidations,
    validation,
    onSubmit: submit,
    loading,
    error,
    setError,
    reset,
    changed,
    valid,
  };
};

export default useForm;
